import React, {useEffect, useMemo} from 'react';
import styled from 'styled-components';
import {fontSize, MediumTitle, wh} from '../../mixins';
import {useDispatch, useSelector} from 'react-redux';
import {selectOfferType} from '../onboarding/onboardingSlice';
import {OrrangeCircle, WhiteCircle} from '../../UI/Icons';
import {selectPlan, setPlan} from './paywallSlice';
import LatamPlan from './LatamPlan';
import OneTimePlan from './OneTimePlan';
import PackagePlans from './PackagePlans';
import PackageOptions from './PackageOptions';


const StyledContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({customPadding}) => customPadding || '70px 0 0'};
  overflow-x: hidden;
  
  @media screen and (max-height: 570px) {
    padding: 30px 0 0;
  }
`;

const StyledTitle = styled.b`
 ${MediumTitle};
 margin-bottom: 24px;
`;

const StyledIcon = styled.span`
  position: absolute;
  top: 33%;
  right: 6%;
`;

const StyledList = styled.ul`
  width: 100%;
  max-width: 345px;
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
`;

const StyledName = styled.span`
  ${fontSize(14)};
  line-height: 22px;
  letter-spacing: -0.225882px;
  font-weight: 500;
`;

const StyledPrice = styled.span`
  ${fontSize(12)};
  line-height: 22px;
  letter-spacing: -0.225882px;
  color: #fff;

  &:first-letter {
    text-transform: uppercase;
  }
`;

const StyledLi = styled.li`
  width: 100%;
  position: relative;
  background: linear-gradient(97.26deg, #454545 0%, #303030 100.55%);
  box-shadow: inset 0 0 0 1px ${({active}) => active ? '#FBA253' : 'transparent'}; 
  box-sizing: border-box;
  border-radius: 8px;
  margin-bottom: 12px;
  
  &:first-child {
    border-radius: 0 0 8px 8px;
    margin-top: 20px;
    
    &:before {
      content: attr(data-value);
      width: 100%;
      height: 20px;
      color: #fff;
      font-weight: 600;
      ${fontSize(10)};
      line-height: 1;
      text-transform: uppercase;
      background-color: #F2994A;
      border-radius: 8px 8px 0 0;
      padding: 5px 16px;
      box-sizing: border-box;
      position: absolute;
      top: -20px;
      left: 0;
    }
  }
  
  button {
    width: 100%;
    height: 100%;
    color: #fff;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    background-color: transparent;
    border: none;
    padding: 11px 16px;
    cursor: pointer;
      
    &:focus {
      outline: none;
    }
  }
`;

const StyledOptions = styled.ul`
  max-width: 345px;
  list-style: none;
  width: 100%;
  background: #454545;
  border-radius: 12px;
  padding: 7px 16px 7px 65px;
`;

const StyledOption = styled.li`
  position: relative;
  ${fontSize(14)};
  line-height: 22px;
  letter-spacing: -0.225882px;
  color: #FFFFFF;
  padding: 10px 0;
  
  svg {
    position: absolute;
    left: -34px;
  }
`;

const StyledButtonWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: sticky;
    bottom: 24px;
    left: 0;
`;

const CustomButton = styled.div`
    ${wh('90%', 'auto')};
    max-width: 327px;
    background: #F09A51;
    color: #FFFFFF;
    ${fontSize(14)};
    line-height: 18px;
    font-weight: 500;
    padding: 14px 20px;
    border-radius: 24px;
    position: relative;
    text-align: center;
`;

const StyledOptionsWrapper = styled.div`
  overflow-x: scroll;
  margin: 10px 0;
  
  @media screen and (min-width: 430px) {
    margin: 30px 0
  }
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledPromoPlan = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: linear-gradient(92.51deg, #EF7A12 18.45%, #FBA253 100%);
    box-shadow: 0 4px 20px rgba(0,0,0,.25);
    border-radius: 16px;
    padding: 12px 0 14px;
    color: #fff;
    font-weight: 800;
    ${fontSize(30)};
    line-height: 1;
    letter-spacing: -0.460567px;
    position: relative;
    
    &:before {
        content: '';
        display: block;
        width: calc(100% + 2px);
        height: calc(100% + 2px);
        position: absolute;
        top: -1px;
        left: -1px;
        background: linear-gradient(90deg, rgba(255,255,255,1), rgba(255,255,255,.4));
        border-radius: inherit;
        z-index: -1;
    }
    
    span:last-child {
        ${fontSize(16)};
        font-weight: normal;
        margin-top: 8px;
        
        &:first-letter {
            text-transform: uppercase;
        }
    }
`;

const StyledPromoTitle = styled.div`
    font-weight: 600;
    ${fontSize(24)};
    line-height: 100%;
    color: #fff;
    letter-spacing: -0.460567px;
    text-transform: uppercase;
    text-align: center;
    margin-bottom: 24px;
`;


const ExperimentalPlans = ({onBuyClick}) => {
    const plan = useSelector(selectPlan);
    const dispatch = useDispatch();
    const offerType = useSelector(selectOfferType);
    const packagePlans = ['optionsg', 'optionsh'];
    const promoPlans = ['promo', 'trial'];
    const isPackage = packagePlans.includes(offerType);
    const isPromoPlans = promoPlans.includes(offerType);
    const isLatam = (offerType === 'latam');
    const isOneTimePlan = (offerType === 'optionsj');

    const PLANS = useMemo(() => {
        return {
            optionsa: [
                {
                    id: 'gz-3-months-intro-banana',
                    title: '3 months',
                    price: '$0.99 for the first 3 days, then $39.99',
                    popular: true
                },
                {
                    id: 'gz-1-month-intro-franko',
                    title: '1 week',
                    price: '$9.99 per week*',
                    note: '*Trial offer, billed at $19.96/mo after'
                },
            ],
            optionsb: [
                {
                    id: 'gz-1-month-intro-kobylanska',
                    title: '1 month',
                    price: '$0.99 for the first 3 days, then $11.99 monthly',
                    popular: true

                },
                {
                    id: 'gz-3-months-red-banana',
                    title: '3 months',
                    price:  '$3.33 /week',
                },
            ],
            optionsd: [
                {
                    id: 'gz-1-month-intro-vynnychenko',
                    title: 'TRIAL OFFER',
                    price: '$0.99 for the first 3 days, then $7.50 weekly',
                    popular: true

                },
                {
                    id: 'gz-3-months-red-banana',
                    title: '3 months',
                    price:  '$3.33 /week',
                },
            ],
            optionse: [
                {
                    id: 'gz-1-month-tychyna',
                    title: '1 month',
                    price: '$2.99 / week',
                    popular: true
                },
                {
                    id: 'gz-3-month-kostenko',
                    title: '3 months',
                    price: '$3.33 / week',
                },
            ],
            optionsf: [
                {
                    id: 'gz-3-month-kostenko',
                    title: '3 months',
                    price: '$3.33 / week',
                    popular: true
                },
                {
                    id: 'gz-1-month-tychyna',
                    title: '1 month',
                    price: '$2.99 / week'
                },
            ],
            intro: [
                {
                    id: 'gz-28-days-vasyl-stus',
                    title: 'FREE TRIAL FOR 3 DAYS',
                    price: 'then $7.99 /week',
                    popular: true

                },
                {
                    id: 'gz-56-days-lesya-ukrainka',
                    title: '2 months',
                    price: '$5.99 /week',
                },
            ],
            latam: [
                {
                    id: 'gz-1-month-tychyna',
                    title: '1 month',
                    oldPrice: '$19.99',
                    price: '$11.97',
                    pricePerWeek: '$2.99',
                    bestseller: true,
                    bestsellerDiscount: '-40%'
                },

                {
                    id: 'gz-3-month-kostenko',
                    title: '3 months',
                    oldPrice: '$49.99',
                    price: '$39.95',
                    pricePerWeek: '$9.98',
                },
            ],
            optionsg: [
                {
                    id: 'gz-1-month-2999',
                    title: 'Premium',
                    period: 'week',
                    saveAmount: '50%',
                    week: 'per week',
                    prevPrice: '$14.99',
                    weeklyPrice: '$7.49',
                    price: '$29.99',
                },
                {
                    id: 'gz-1-month-tomato',
                    title: 'Basic',
                    period: 'week',
                    week: 'per week',
                    prevPrice: '$9.99',
                    weeklyPrice: '$4.99',
                    price: '$19.96',
                },
            ],
            optionsh: [
                {
                    id: 'gz-1-month-tiger',
                    title: 'Premium',
                    period: 'week',
                    saveAmount: '50%',
                    week: 'per week',
                    prevPrice: '$14.99',
                    weeklyPrice: '$9.99',
                    price: '$39.96',
                },
                {
                    id: 'gz-1-month-2999',
                    title: 'Basic',
                    period: 'week',
                    week: 'per week',
                    prevPrice: '$9.99',
                    weeklyPrice: '$7.49',
                    price: '$29.99',
                },
            ],
            optionsj: [
                {
                    id: 'gz-1-month-2999',
                    title: 'Unlock all features for',
                    period: 'week',
                    week: 'per week',
                    prevPrice: '$9.99',
                    weeklyPrice: '$7.49',
                    price: '$29.99'
                }
            ],
            promo: [
                {
                    id: 'gz-monthly-free3d-2999',
                    title: '3 DAYS FREE',
                    price: 'Then $7.49 per week',
                }
            ],
            trial: [
                {
                    id: 'gz-monthly-free3d-2999',
                    title: '3  DAYS FREE',
                    price: 'Then $7.49 per week',
                    bestseller: true,
                },
                {
                    id: 'gz-3-months-red-banana',
                    title: '3 month',
                    price: '$3.33 / week',
                },
            ]
        };
    }, []);

    const onPlanClick = id => dispatch(setPlan(id));

    const currentPlans = useMemo(() => PLANS[offerType], [offerType, PLANS]);

    useEffect(() => {
        dispatch(setPlan(currentPlans[0].id));
    }, [dispatch, currentPlans]);

    const renderOffer = (i, index) => {
        const isActive = plan === i.id;

        return <React.Fragment key={i.id}>
            <StyledLi active={isActive} data-value='MOST POPULAR'>
                <button onClick={() => onPlanClick(i.id)} id={'plan-button-' + (index + 1)}>
                    <StyledName>{i.title.toUpperCase()}</StyledName>
                    <StyledPrice>{i.price}</StyledPrice>
                    <StyledIcon>
                        {isActive ? OrrangeCircle : WhiteCircle}
                    </StyledIcon>
                </button>
            </StyledLi>
            {plan === 'gz-1-month-intro-franko' && i.note && (
                <StyledPrice>{i.note}</StyledPrice>
            )}
        </React.Fragment>;
    };

    const renderPromoPlan = (() => {
        const {title,price} = currentPlans[0];
        return <StyledPromoPlan>
            <span>{title}</span>
            <span>{price}</span>
        </StyledPromoPlan>;
    })();

    const buttonWrapper = <StyledButtonWrapper landingType={offerType}>
        <CustomButton onClick={onBuyClick}>Continue</CustomButton>
    </StyledButtonWrapper>;

    if (isLatam) {
        return <>
            <LatamPlan
                currentPlans={currentPlans}
                onPlanClick={onPlanClick}
                plan={plan}
            />
            {buttonWrapper}
        </>;
    }

    if (isOneTimePlan) {
        return <>
            <OneTimePlan currentPlans={currentPlans} />
            {buttonWrapper}
        </>;
    }

    if (isPackage) {
        return <>
            <PackagePlans
                currentPlans={currentPlans}
                onPlanClick={onPlanClick}
                plan={plan}
                landingType={offerType}
            />
            {buttonWrapper}
        </>;
    }

    if (isPromoPlans) {
        return <>
            <StyledContainer customPadding={'30px 10px 0'}>
                <StyledPromoTitle>UNLOCK ALL FEATURES</StyledPromoTitle>
                {(offerType === 'promo')
                    ? renderPromoPlan
                    : <StyledList>
                        {currentPlans.map(renderOffer)}
                    </StyledList>
                }
                <StyledOptionsWrapper>
                    <PackageOptions active={true} promo={true}/>
                </StyledOptionsWrapper>
            </StyledContainer>
            {buttonWrapper}
        </>;
    }

    return (
        <>
            <StyledContainer>
                <StyledTitle>Unlock All Access</StyledTitle>
                <StyledList>
                    {currentPlans.map(renderOffer)}
                </StyledList>
                <StyledOptions>
                    <StyledOption>
                        {OrrangeCircle}
                        Unlimited Place Alerts</StyledOption>
                    <StyledOption>
                        {OrrangeCircle}
                        Unlimited Live-Locating</StyledOption>
                    <StyledOption>
                        {OrrangeCircle}
                        Location History</StyledOption>
                </StyledOptions>
            </StyledContainer>
            {buttonWrapper}
        </>
    );
};

export default ExperimentalPlans;